import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Stethoscope, FileText, AlertTriangle, Clock, ChevronRight, Activity } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await dashboardAPI.stats();
      setStats(res.data);
    } catch (err) {
      console.error('Dashboard stats error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getHealingBadge = (report) => {
    if (!report) return null;
    const signs = report.infektionszeichen || [];
    if (signs.length > 0) return { label: 'Kritisch', className: 'bg-rose-50 text-rose-700 border-rose-200' };
    const nrs = report.schmerz_nrs || 0;
    if (nrs >= 6) return { label: 'Beobachten', className: 'bg-amber-50 text-amber-700 border-amber-200' };
    return { label: 'Stabil', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return d; }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Hallo, {user?.name?.split(' ')[0] || 'Team'}
        </h1>
        <p className="text-slate-500 mt-1">
          {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => navigate('/patienten')} data-testid="stat-patients">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-teal-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_patients || 0}</p>
                <p className="text-xs text-slate-500 font-medium">Patienten</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => navigate('/patienten?filter=mit-wunden')} data-testid="stat-wounds">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_wounds || 0}</p>
                <p className="text-xs text-slate-500 font-medium">Akt. Wunden</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => navigate('/patienten?filter=mit-wunden')} data-testid="stat-reports">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_reports || 0}</p>
                <p className="text-xs text-slate-500 font-medium">Berichte</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => navigate('/patienten?filter=faellige-vw')} data-testid="stat-due">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.due_bandage_changes || 0}</p>
                <p className="text-xs text-slate-500 font-medium">Fällige VW</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Button
          data-testid="quick-new-report"
          onClick={() => navigate('/neuer-bericht')}
          className="h-14 bg-teal-700 hover:bg-teal-800 text-white justify-start px-5 rounded-xl text-base"
        >
          <FileText className="w-5 h-5 mr-3" />
          Neuen Wundbericht erstellen
        </Button>
        <Button
          data-testid="quick-new-patient"
          variant="outline"
          onClick={() => navigate('/patienten/neu')}
          className="h-14 justify-start px-5 rounded-xl text-base border-slate-200"
        >
          <Users className="w-5 h-5 mr-3" />
          Neuen Patienten anlegen
        </Button>
      </div>

      {/* Recent Activity */}
      <Card className="border border-slate-200 shadow-sm" data-testid="recent-reports">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-teal-700" />
              Letzte Aktivitäten
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {stats?.recent_reports?.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_reports.slice(0, 6).map((r) => {
                const badge = getHealingBadge(r);
                return (
                  <div
                    key={r.id}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-all cursor-pointer group"
                    onClick={() => r.wound_id && navigate(`/wunde/${r.wound_id}`)}
                    data-testid={`recent-report-${r.id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{r.patient_name}</p>
                      <p className="text-xs text-slate-500">{r.wundart} &middot; {r.wound_lokalisation}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {badge && (
                        <Badge variant="outline" className={`text-[10px] border ${badge.className}`}>
                          {badge.label}
                        </Badge>
                      )}
                      <span className="text-xs text-slate-400 hidden sm:block">
                        {formatDate(r.datum)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 transition-colors" />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Noch keine Berichte vorhanden</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
